'use client';

import {
	EventStore,
	isDev,
	noop,
	sendAppState,
	sessionAtom,
	TrackingEventType,
	useAnalytics,
	useIntl,
	useLogout,
	useOnMount,
	useSubscribe
} from '@capsicum/common';
import {
	AppTopBar,
	Drawer,
	type DrawerProfileProps,
	isClientSide,
	LayoutContainer,
	Splash,
	useAppState,
	useAutoDeviceLocaleSetup,
	useNavRailItems,
	useRouter,
	useWebPush,
	withRouteGuard
} from '@capsicum/common-web';
import { SsuBalance } from '@capsicum/features/wallet/web';
import { useMe } from '@capsicum/socios-core';
import { DeeplinkRedirectGuard, UserGuard } from '@capsicum/socios-core-web';
import { SociosBlkSdk } from '@socios-blk/core';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import Package from './../../package.json';
import { useDrawerContent } from './drawer';
import AuthenticatedInitFlow from './init';

// Issue: https://github.com/microsoft/TypeScript-DOM-lib-generator/issues/1615
type ExtendedScreenOrientation = ScreenOrientation & { lock: (orientation: 'landscape' | 'portrait') => Promise<void> };

const InnerLayout = withRouteGuard(
	({
		children // will be a page or nested layout
	}: {
		children: React.ReactNode;
	}) => {
		const { requestInitialPermission } = useWebPush();

		if (
			isClientSide() &&
			window.screen.orientation &&
			(window.screen.orientation as ExtendedScreenOrientation).lock
		) {
			(window.screen.orientation as ExtendedScreenOrientation).lock('portrait').catch(noop);
		}
		useAutoDeviceLocaleSetup();
		const analytics = useAnalytics();
		const { me } = useMe();
		const navRailItems = useNavRailItems();
		const { $t } = useIntl();
		const { redirectTo, ...drawerProps } = useDrawerContent({ isDev: isDev(), useRouter });
		const sessionTokens = useAtomValue(sessionAtom);
		const authToken = sessionTokens?.authToken;

		useEffect(() => {
			if (!SociosBlkSdk.isInitialized() || !authToken) {
				return;
			}

			SociosBlkSdk.setAuthToken(authToken);
		}, [authToken]);

		useAppState({
			onResumeApp: () => {
				requestInitialPermission();
				sendAppState();
			}
		});
		useOnMount(() => {
			analytics.sendEvent(TrackingEventType.identify, me.externalUserId || '');

			sendAppState();
		});

		const profileProps: DrawerProfileProps = {
			activityButtonText: $t('profile.activityButton'),
			navigate: redirectTo
		};

		return (
			<AuthenticatedInitFlow>
				<div>
					<AppTopBar SSUBalance={SsuBalance} />
					<LayoutContainer navRailItems={navRailItems}>{children}</LayoutContainer>
					<Drawer appVersion={Package.version} profileProps={profileProps} {...drawerProps} />
				</div>
			</AuthenticatedInitFlow>
		);
	},
	[UserGuard, DeeplinkRedirectGuard],
	Splash
);

export default function Layout({ children }: { children: React.ReactNode }) {
	const logout = useLogout();

	useSubscribe(EventStore.SESSION_EXPIRED, logout);

	return <InnerLayout>{children}</InnerLayout>;
}
